import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { ProducerOffering, InteractionHint } from '@/lib/producer/offeringTypes';

/**
 * Hook to read and write producer offerings from the normalized
 * producer_offerings join table.
 *
 * Returns the current offering keys as string[] and full rows,
 * plus save functions for both simple keys and detailed offerings.
 */
export function useProducerOfferings(producerId: string | undefined) {
  const queryClient = useQueryClient();
  const queryKey = ['producer-offerings', producerId];

  const query = useQuery({
    queryKey,
    enabled: !!producerId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('producer_offerings')
        .select('producer_id, offering_key, interaction_hint, note, sort_order, is_enabled, created_at, updated_at')
        .eq('producer_id', producerId!)
        .order('sort_order');
      if (error) throw error;
      return (data ?? []) as ProducerOffering[];
    },
  });

  const invalidateRelated = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
    queryClient.invalidateQueries({ queryKey: ['producer', producerId] });
    queryClient.invalidateQueries({ queryKey: ['producer-capabilities', producerId] });
  }, [producerId, queryClient, queryKey]);

  /**
   * Simple save: replace all offerings with a list of keys.
   * Preserves interaction_hint/note for keys that already existed.
   */
  const saveOfferings = useCallback(
    async (nextKeys: string[]) => {
      if (!producerId) return;
      const existing = query.data ?? [];
      const existingMap = new Map(existing.map((r) => [r.offering_key, r]));

      const { error: delErr } = await supabase
        .from('producer_offerings')
        .delete()
        .eq('producer_id', producerId);
      if (delErr) throw delErr;

      if (nextKeys.length > 0) {
        const rows = nextKeys.map((key, idx) => {
          const prev = existingMap.get(key);
          return {
            producer_id: producerId,
            offering_key: key,
            interaction_hint: prev?.interaction_hint ?? null,
            note: prev?.note ?? null,
            sort_order: idx,
            is_enabled: true,
          };
        });
        const { error: insErr } = await supabase
          .from('producer_offerings')
          .insert(rows);
        if (insErr) throw insErr;
      }

      invalidateRelated();
    },
    [producerId, query.data, invalidateRelated],
  );

  /**
   * Detailed save: replace all offerings with full row data.
   */
  const saveOfferingsDetailed = useCallback(
    async (nextOfferings: Array<{
      offering_key: string;
      interaction_hint?: InteractionHint | null;
      note?: string | null;
      sort_order?: number;
    }>) => {
      if (!producerId) return;

      const { error: delErr } = await supabase
        .from('producer_offerings')
        .delete()
        .eq('producer_id', producerId);
      if (delErr) throw delErr;

      if (nextOfferings.length > 0) {
        const rows = nextOfferings.map((o, idx) => ({
          producer_id: producerId,
          offering_key: o.offering_key,
          interaction_hint: o.interaction_hint ?? null,
          note: o.note ?? null,
          sort_order: o.sort_order ?? idx,
          is_enabled: true,
        }));
        const { error: insErr } = await supabase
          .from('producer_offerings')
          .insert(rows);
        if (insErr) throw insErr;
      }

      invalidateRelated();
    },
    [producerId, invalidateRelated],
  );

  const offerings = (query.data ?? [])
    .filter((r) => r.is_enabled)
    .map((r) => r.offering_key);

  return {
    /** Simple list of active offering keys */
    offerings,
    /** Full offering rows with interaction_hint, note, etc. */
    offeringRows: query.data ?? [],
    isLoading: query.isLoading,
    /** Simple save (keys only, preserves existing metadata) */
    saveOfferings,
    /** Detailed save (full offering data) */
    saveOfferingsDetailed,
    refetch: query.refetch,
  };
}
